import React, { useState } from 'react';

import { message, Modal, Card,  Col, Form, Row, Table, Space } from 'antd';
import type { TableProps } from 'antd';
import { Button, Select, Input, Checkbox } from 'antd';
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';

import { CustomerSelect } from '../../features/customer/components/customer-select';
import { CustomerForm } from '../../customer/components/CustomerForm';
import { ArtworkSearchModal } from '../../features/artworks/components/artwork-search-modal';
import { useModal } from '../../components/hooks/useModal';
import { CountrySelect } from '../../components/select/CountrySelect';
import { DateUpdateFlexRow } from '../../components/widgets/DateUpdateFlexRow';

import { ArtworkItem } from '../../hooks/data/models';
import { Customer } from '../../hooks/data/models';
import { useCustomers } from '../../hooks/data/useCustomers';
import { useOrders } from '../../hooks/data/useOrders';
import { useArtworks } from '../../hooks/data/useArtworks';

interface Props {
  onSubmit: (values: any) => Promise<any>;
}

export const CreateOrderForm: React.FunctionComponent<Props> = ({ onSubmit }) => {
  const [sameAddress, setSameAddress] = useState(true);
  const { visible, openModal, closeModal } = useModal();
  // List of all available artworks which might be added to the order
  const {
    artworks: availableArtworks, 
    isLoading: availableArtworksLoading,
    lastUpdated: availableArtworksLastUpdate, 
    refreshMainList: refreshAvailableArtworksMainList} = useArtworks();

  // Order Artworks
  const [artworks, setArtworks] = useState<ArtworkItem[]>([]);
  const [currency, setCurrency] = useState('EUR');
  const { refreshMainList: refreshOrdersMainList } = useOrders();
  // New Customer
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [isCustomerFormDisabled, setIsCustomerFormDisabled] = useState(false);
  const [isSavingCustomer, setIsSavingCustomer] = useState(false);
  const [customerForm] = Form.useForm();
  const { createCustomer } = useCustomers();

  // Copy shipping address from customer to order
  const handleCopyShippingAddressFromCustomer = () => {
    if(!selectedCustomer){
      message.warning("No customer selected!");
      return;
    }
    if(!selectedCustomer.shippingAddress){
      message.warning("No shipping address defined for the selected customer!")
      return;
    }
    
    form.setFieldsValue({
      shippingAddressLine1: selectedCustomer.shippingAddress.line1,
      shippingAddressLine2: selectedCustomer.shippingAddress.line2,
      shippingAddressPostalCode: selectedCustomer.shippingAddress.postalCode,
      shippingAddressCity: selectedCustomer.shippingAddress.city,
      shippingAddressState: selectedCustomer.shippingAddress.state,
      shippingAddressCountry: selectedCustomer.shippingAddress.country,
    });
  }
  // Copy billing address from customer to order
  const handleCopyBillingAddressFromCustomer = () => {
    if(!selectedCustomer){
      message.warning("No customer selected!");
      return;
    }
    if(!selectedCustomer.billingAddress){
      message.warning("No billing address defined for the selected customer!")
      return;
    }
    
    form.setFieldsValue({
      billingAddress: selectedCustomer.billingAddress
    });
  }
  // Copy shipping address to billing address in customer form
  const handleNewCustomerAddressCopyClick = () => {
    customerForm.setFieldsValue({
      billingAddress: customerForm.getFieldsValue(['shippingAddress'])['shippingAddress']
    });
  }
  const handleSaveNewCustomerClick = async () => {
    try {
      setIsSavingCustomer(true);
      setIsCustomerFormDisabled(true);
      // Validate all fields in the form
      const values = await customerForm.validateFields();
      //console.log('Form values validated:', values);

      // Prepare data for the API call 
      const createPayload = {
        ...values, // Spread the validated form values
      };

      // Create customer and optimistically update the customer list
      await createCustomer(createPayload);
      message.success("Customer created!");
      setCustomerModalOpen(false);
    } catch (error: any) {
      let generalMessage = 'Failed to create customer';
      console.error(generalMessage.concat(': ', error));
      setIsCustomerFormDisabled(false);
      if (error.response && error.response.data && error.response.data.message) {
        let errorMessage = error.response.data.message;
        
        if(Array.isArray(errorMessage)){
          //handle validation error
          message.error(`Invalid field: ${errorMessage.map(el => {return el.property})}`);
        }
        else{
          message.error(generalMessage.concat(". ", errorMessage));
        }
      } else {
        message.error(generalMessage.concat('. Unhandled exception.'));
      }
    } finally {
      setIsSavingCustomer(false); // Reset saving state
    }
  };

  // Order form
  const [form] = Form.useForm();

  const handleSubmit = ({ shipping, ...values }: any) => {
    const {
      customer,
      shippingAddressLine1,
      shippingAddressLine2,
      shippingAddressPostalCode,
      shippingAddressCity,
      shippingAddressState,
      shippingAddressCountry,
      ...data
    } = values;

    onSubmit({
      customerId: customer._id,
      lineItems: artworks,
      shippingAddress: {
        line1: shippingAddressLine1,
        line2: shippingAddressLine2,
        postalCode: shippingAddressPostalCode,
        city: shippingAddressCity,
        state: shippingAddressState,
        country: shippingAddressCountry,
      },
      shippingTotal: parseFloat(shipping),
      currency,
      ...data,
    }).then(() => {
      refreshOrdersMainList();
    });
  };

  const handleAddArtwork = (artwork: ArtworkItem) => {
    const isAlreadyAdded = !!artworks.find((el) => el._id === artwork._id);

    if (isAlreadyAdded) {
      return;
    }

    setArtworks([...artworks, artwork]);
    closeModal();
  };
  
  const handleRemoveArtwork = (artwork: ArtworkItem) => {
    const idToRemove = artwork._id;
    setArtworks(currentItems => currentItems.filter(item => item._id !== idToRemove));
  };
  
  //Table rendering variables
  const columns: TableProps<ArtworkItem>['columns'] = [
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year'
    },
    {
      title: 'Artwork Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Actions',
      render: (_, record) => (      
          <DeleteOutlined onClick={() => handleRemoveArtwork(record)}/> 
        )
    }
  ]

  return (
    <Form layout={'vertical'} form={form} onFinish={handleSubmit}>

      <Space direction='vertical' style={{display: 'flex'}}>
        
        <Form.Item>
          <Button htmlType="submit" type='primary'>Save Order</Button>
        </Form.Item>

        <Row gutter={[32, 24]}>
          <Col xs={24} lg={24} xxl={24}>          
            <Card title={'Customer'} extra={<Button onClick={() => {setCustomerModalOpen(true)}}> New Customer </Button>}>
              <Form.Item name={'customer'}>
                <CustomerSelect onChange={(data: Customer) => {
                  setSelectedCustomer(data);
                }}/>
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12} xxl={16}>
              <Card title={'Artworks'} extra={[<Button onClick={openModal} disabled={availableArtworksLoading} key="add-item-button">Add item</Button>]}>
                <DateUpdateFlexRow lastUpdated={availableArtworksLastUpdate} refreshCallback={refreshAvailableArtworksMainList}/>
                <Table columns={columns} dataSource={artworks} rowKey={record => record._id}></Table>
              </Card>
          </Col>

          <Col xs={{span: 24}} lg={{span: 12}} xxl={{span: 8}}>
            <Space direction='vertical' style={{ display: 'flex' }}>
              <Card title={'Currency'}>
                <Form.Item name="currency">
                  <Select value={currency} onChange={setCurrency}>
                    <Select.Option value="EUR">EUR</Select.Option>
                    <Select.Option value="CHF">CHF</Select.Option>
                    <Select.Option value="GBP">GBP</Select.Option>
                    <Select.Option value="USD">USD</Select.Option>
                  </Select>
                </Form.Item>
              </Card>
              <Card title={'Shipping'}>
                <Form.Item label="Shipping Price" name={'shipping'}>
                  <Input />
                </Form.Item>
              </Card> 
            </Space>
          </Col>

          <Col xs={{span: 24}} lg={{span: 12}}>      
            <Card 
              title={'Order Shipping address'} 
              extra={
                <CopyOutlined 
                  title="Copy from customer"
                  onClick={handleCopyShippingAddressFromCustomer}
                  style={{cursor: 'pointer'}}
                />}
            >
              <Form.Item
                label="Line 1"
                name={'shippingAddressLine1'}
                rules={[
                  {
                    required: true,
                    message: 'Shipping address is required',
                  },
                ]}>
                <Input />
              </Form.Item>

              <Form.Item label="Line 2" name="shippingAddressLine2">
                <Input />
              </Form.Item>

              <Form.Item label="Postal Code" name="shippingAddressPostalCode">
                <Input />
              </Form.Item>

              <Form.Item label="City" name="shippingAddressCity">
                <Input />
              </Form.Item>

              <Form.Item label="State" name="shippingAddressState">
                <Input />
              </Form.Item>

              <Form.Item label="Country" name="shippingAddressCountry">
                <CountrySelect />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={{span: 24}} lg={{span: 12}}>
            <Card title={'Order Billing address'}
              extra={
                <Space >
                  <CopyOutlined 
                    title="Copy from customer"
                    onClick={handleCopyBillingAddressFromCustomer}
                    style={{cursor: 'pointer'}}
                  />
                  <Checkbox
                    onChange={(e) => setSameAddress(e.target.checked)}
                    checked={sameAddress}
                    defaultChecked>
                    Use shipping address
                  </Checkbox>
                </Space>
                
              }>
              {!sameAddress && (
                <>
                  <Form.Item label="Line 1" name={["billingAddress","line1"]}>
                    <Input />
                  </Form.Item>

                  <Form.Item label="Line 2" name={["billingAddress","line2"]}>
                    <Input />
                  </Form.Item>

                  <Form.Item label="Postal Code" name={["billingAddress","postalCode"]}>
                    <Input />
                  </Form.Item>

                  <Form.Item label="City" name={["billingAddress","city"]}>
                    <Input />
                  </Form.Item>

                  <Form.Item label="State" name={["billingAddress","state"]}>
                    <Input />
                  </Form.Item>

                  <Form.Item label="Country" name={["billingAddress","country"]}>
                    <CountrySelect />
                  </Form.Item>
                </>
              )}
            </Card>
          </Col>
          
        </Row>
      </Space>

      <ArtworkSearchModal onSubmit={handleAddArtwork} onCancel={closeModal} visible={visible} availableArtworks={availableArtworks}/>
    
      <Modal
        title="New customer"
        open={customerModalOpen}
        onCancel={() => setCustomerModalOpen(false)}
        okText={"Save"}
        okButtonProps={{loading: isSavingCustomer}}
        onOk={handleSaveNewCustomerClick}
        width={{
          xs: '90%',
          sm: '90%',
          md: '70%',
          lg: '60%',
          xl: '50%',
          xxl: '40%',
        }}
      >
        <CustomerForm 
          customerForm={customerForm}
          copyAddressCallback={handleNewCustomerAddressCopyClick}
          formDisabled={isCustomerFormDisabled}></CustomerForm>
      </Modal>
    </Form>
  );
};
